import { ChangeDetectionStrategy, Component, computed, inject, input, model, output, signal } from '@angular/core';

export interface SelectButtonOption {
  label: string;
  value: any;
  icon?: string;
}

@Component({
  selector: 'app-select-button',
  template: `
    <div [class]="containerClasses()" [attr.role]="'group'" [attr.aria-label]="ariaLabel()">
      @for (option of options(); track option.value) {
        <button
          type="button"
          [class]="buttonClasses(option)"
          (click)="select(option.value)"
          [attr.aria-pressed]="isSelected(option.value)"
          [disabled]="disabled()"
        >
          @if (option.icon) {
            <i [class]="option.icon + ' mr-1'" aria-hidden="true"></i>
          }
          {{ option.label }}
        </button>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UiSelectButton {
  readonly options = input<SelectButtonOption[]>([]);
  readonly disabled = input<boolean>(false);
  readonly ariaLabel = input<string>('');
  readonly styleClass = input<string>('');

  readonly value = model<any>(null);
  readonly onChange = output<any>();

  protected containerClasses(): string {
    return `inline-flex rounded-lg border border-slate-200 overflow-hidden shadow-sm ${this.styleClass()}`;
  }

  protected buttonClasses(option: SelectButtonOption): string {
    const base = 'px-3 py-1.5 text-sm font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed';
    const selected = this.isSelected(option.value)
      ? 'bg-accent-600 text-white'
      : 'bg-white text-slate-700 hover:bg-slate-50';
    return `${base} ${selected}`;
  }

  protected isSelected(value: any): boolean {
    return this.value() === value;
  }

  select(value: any): void {
    if (this.disabled()) return;
    this.value.set(value);
    this.onChange.emit(value);
  }
}
